import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import IconButton from 'fe-design-base/molecules/IconButton';
import Tabs from 'fe-design-base/molecules/Tabs';

import {
  fetchNotifications,
  toggleNotificationsShown,
} from 'actions/notifications';

import { buildGetDrawerOpen } from 'selectors/drawers';
import {
  getAlertsItems,
  getHasMoreAlerts,
  getHasMoreRequests,
  getIsFetchingNotifications,
  getRequestsItems,
} from 'selectors/notifications';
import {
  getCurrentLocationUuid,
  getCurrentUserIsManagerial,
  getRequestsFeedExperiment,
} from 'selectors/session';

import { NOTIFICATIONS_DRAWER_KEY } from 'features/drawers/keys';
import { enrollEntityInExperiment } from 'features/experiments/actions';
import { MyRequests } from 'features/notifications/components/MyRequests';
import { NotificationsHeader } from 'features/notifications/components/NotificationsHeader';
import { TeamRequests } from 'features/notifications/components/TeamRequests';
import {
  clearSelectedRequest,
  fetchUnreadCounts,
} from 'features/notifications/slice';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';

import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  trackNotificationEvents,
} from '../tracking';

import NotificationsList from './NotificationsList';

const { cxEl } = cxHelpers('NotificationView');

const NAV_HEADERS = [
  {
    value: 'requests',
    label: toI18n('notifications.requests.title'),
  },
  {
    value: 'alerts',
    label: toI18n('notifications.alerts.title'),
  },
];

const EMPLOYEE_NAV_HEADERS = [NAV_HEADERS[0]];

const requestsFeedWidth = 450;
const requestsWidth = 390;

const DRAWER_CONTAINER_STYLE = {
  width: requestsWidth,
};

const DRAWER_CONTAINER_STYLE_REQUESTS_FEED = {
  width: requestsFeedWidth,
};

const NotificationsView = () => {
  const dispatch = useDispatch();
  const requestsFeedEnabled = useSelector(getRequestsFeedExperiment);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [tabValue, setTabValue] = useState<string>(
    requestsFeedEnabled ? 'team-requests' : 'requests'
  );
  const isManagerial = useSelector(getCurrentUserIsManagerial);
  const currentLocation = useSelector(getCurrentLocationUuid);

  const isOpen = useSelector(buildGetDrawerOpen(NOTIFICATIONS_DRAWER_KEY));
  const requestsItems = useSelector(getRequestsItems);
  const alertsItems = useSelector(getAlertsItems);
  const isFetching = useSelector(getIsFetchingNotifications);
  const hasMoreRequests = useSelector(getHasMoreRequests);
  const hasMoreAlerts = useSelector(getHasMoreAlerts);

  useEffect(() => {
    // this thunk was not typed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    enrollEntityInExperiment(['requests_feed']);
  }, []);

  useEffect(() => {
    if (requestsFeedEnabled) {
      dispatch(fetchUnreadCounts(currentLocation));
    }
  }, [dispatch, currentLocation, requestsFeedEnabled]);

  useEffect(() => {
    if (!isManagerial) {
      if (!requestsFeedEnabled && tabValue !== 'requests') {
        setTabValue('requests');
      } else if (requestsFeedEnabled && tabValue !== 'my-requests') {
        setTabValue('my-requests');
      }
    }
  }, [isManagerial, requestsFeedEnabled, tabValue]);

  const handleClose = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.CLOSE_CLICKED,
    });
    dispatch(toggleNotificationsShown(false));
  }, [dispatch]);

  const handleAlertsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.ALERTS_CLICKED,
    });
  }, []);

  const handleRequestsTabClick = useCallback(() => {
    trackNotificationEvents({
      eventAction: EVENT_ACTIONS.REQUESTS_CLICKED,
    });
  }, []);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: string | number) => {
      setTabValue(newValue as string);
      if (requestsFeedEnabled) {
        dispatch(clearSelectedRequest());
      }
      const handleTabClick =
        newValue === toI18n('notifications.requests.title')
          ? handleRequestsTabClick
          : handleAlertsTabClick;
      let buttonText =
        newValue === 'team-requests'
          ? toI18n('requests_feed.tabs.team')
          : toI18n('requests_feed.tabs.my');
      if (newValue === 'alerts') {
        buttonText = toI18n('requests_feed.tabs.alerts');
      }
      if (requestsFeedEnabled) {
        trackUxEvent({
          productArea: PRODUCT_AREAS.REQUESTS,
          eventCategory: EVENT_CATEGORIES.REQUESTS_FEED,
          eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
          properties: {
            button_text: buttonText,
          },
        });
      } else {
        handleTabClick();
      }
    },
    [
      handleRequestsTabClick,
      handleAlertsTabClick,
      requestsFeedEnabled,
      dispatch,
    ]
  );

  const getScrollContainer = useCallback(() => scrollContainerRef.current, []);

  const getHeaders = useCallback(
    () =>
      (isManagerial ? NAV_HEADERS : EMPLOYEE_NAV_HEADERS).map(content => {
        const collection =
          content.label === toI18n('notifications.requests.title')
            ? requestsItems
            : alertsItems;

        return {
          value: content.value,
          label: content.label,
          badgeText: collection.size > 0 ? collection.size : undefined,
          badgeVariant: 'loud' as const,
        };
      }),
    [isManagerial, requestsItems, alertsItems]
  );

  const handleFetchNotifications = useCallback(
    (params: any) => {
      if (params?.type === 'alerts' && !isManagerial) return;
      dispatch(fetchNotifications(params));
    },
    [dispatch, isManagerial]
  );

  const renderNotificationsList = useCallback(
    (type: 'requests' | 'alerts', items: any) => (
      <Box key={type}>
        <NotificationsList
          notificationType={type}
          notificationsItems={items}
          fetchNotifications={handleFetchNotifications}
          isFetching={isFetching}
          hasMore={type === 'requests' ? hasMoreRequests : hasMoreAlerts}
          getScrollContainer={getScrollContainer as () => HTMLElement}
          onClose={handleClose}
        />
      </Box>
    ),
    [
      isFetching,
      hasMoreRequests,
      hasMoreAlerts,
      getScrollContainer,
      handleClose,
      handleFetchNotifications,
    ]
  );

  return (
    <Drawer
      containerStyle={
        requestsFeedEnabled
          ? DRAWER_CONTAINER_STYLE_REQUESTS_FEED
          : DRAWER_CONTAINER_STYLE
      }
      open={isOpen}
      className={cxEl('')}
    >
      <Box column maxh="100%" pr={16}>
        {requestsFeedEnabled ? (
          <NotificationsHeader
            tabValue={tabValue}
            handleTabChange={handleTabChange}
            handleClose={handleClose}
          />
        ) : (
          <Box row alignItemsCenter gap={8}>
            <Box grow={1}>
              <Tabs
                noPadding
                onChange={handleTabChange}
                tabValue={tabValue}
                options={getHeaders()}
              />
            </Box>
            <div>
              <IconButton
                onClick={handleClose}
                icon="Close"
                label={toI18n('notifications.close_drawer')}
                className={cxEl('close-button')}
                size="small"
              />
            </div>
          </Box>
        )}

        <Box
          scrollY
          w={requestsFeedEnabled ? requestsFeedWidth : requestsWidth}
        >
          {tabValue === 'requests' &&
            !requestsFeedEnabled &&
            renderNotificationsList('requests', requestsItems)}
          {tabValue === 'team-requests' &&
            requestsFeedEnabled &&
            isOpen &&
            isManagerial && <TeamRequests />}
          {tabValue === 'my-requests' && requestsFeedEnabled && isOpen && (
            <MyRequests />
          )}
          {isManagerial &&
            tabValue === 'alerts' &&
            renderNotificationsList('alerts', alertsItems)}
        </Box>
      </Box>
    </Drawer>
  );
};

export default NotificationsView;
